import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Button, Modal, Form } from 'react-bootstrap';
import { HomeOutlined } from '@ant-design/icons';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import { LimsFeedbackModal } from '../../components/LimsFeedbackModal';
import '../MemberManagement.css';
import { ApprovalFlowService } from '../../services/approvalFlowService';
import { getMyApprovalApplications, submitApprovalApplication, uploadApprovalAttachmentFile, getAuthorizedMerchants, getShippingAddresses, getMembers, SavedShippingAddress } from '../../services/api';
import { ApprovalApplicationExtended } from '../../types/approval';
import { lineTotalYuanFromStrings } from '../../utils/money';
import { useAuth } from '../../contexts/AuthContext';
import { getDemoRecentSubmissions } from '../../demo/demoBrowseMocks';
import {
  PROCUREMENT_PREFILL_STATE_KEY,
  ProcurementPrefillState
} from '../../types/procurementPrefill';
import { getReagentBrandOptions } from '../../constants/reagentBrands';
import { getConsumableBrandOptions } from '../../constants/consumableBrands';

const TEACHER_APPROVER_UNIT_PRICE_THRESHOLD = 500;

const resolveUploadedImageUrl = (url: string) => {
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/uploads/')) return `/api${url}`;
  return url;
};

type TeamTeacherOption = {
  id: string;
  name: string;
};

interface ApprovalApplication {
  key: string;
  title: string;
  category:
    | '物品申领'
    | '耗材申请'
    | '试剂申请'
    | '用章申请'
    | '物品报修'
    | '请假'
    | '测试申请'
    | '出差申请'
    | '财务报销';
  status: '待审批' | '已通过' | '已驳回' | '已撤回';
  applicant: string;
  applyTime: string;
  priority: '普通' | '紧急' | '特急';
  reason: string;
  reasonImages?: Array<{ name: string; url: string }>;
  // 试剂申请字段
  reagentProjects?: Array<{
    category: string;
    image?: string;
    name: string;
    cas: string;
    catalogNumber?: string;
    vendor: string;
    specification: string;
    unit: string;
    purchaseChannel: string;
    amount: string;
    price?: string;
    total?: string;
    usage?: string;
  }>;
  // 耗材申请字段
  consumableProjects?: Array<{
    image?: string;
    name: string;
    model: string;
    brand?: string;
    unit: string;
    purchaseChannel: '网购' | '其他';
    purchaseLink?: string;
    supplier?: string;
    amount: string;
    price?: string;
    total?: string;
    usage?: string;
  }>;
  // 请假申请字段
  leaveStart?: string;
  leaveEnd?: string;
  // 测试申请字段
  testPlatform?: string;
  testProjects?: Array<{
    project: string;
    unitPrice: string;
    quantity: string;
    totalAmount: string;
  }>;
  testTotalAmount?: string;
  // 出差申请字段
  destination?: string;
  tripStartDate?: string;
  tripEndDate?: string;
  tripDays?: string;
  tripPurpose?: string;
  tripBudget?: string;
  // 财务报销
  financeReimbursement?: {
    claimAmount?: number;
    currency?: string;
    expenseType?: string;
    remark?: string;
    attachments?: Array<{ name: string; url: string }>;
  };
  // 审批记录
  approvalRecords?: Array<{
    node: string;
    approver: string;
    result: string;
    time: string;
  }>;
}

// 重置表单数据的函数
const resetApplicationForm = () => ({
  // 通用
  title: '',
  reason: '',
  reasonImages: [] as Array<{ name: string; url: string }>,
  amount: '',
  priority: '普通' as ApprovalApplication['priority'],
  // 耗材申请
  consumableProjects: [{
    category: '小组耗材',
    image: '',
    name: '',
    model: '',
    brand: '',
    unit: '',
    purchaseChannel: '网购' as '网购' | '其他',
    purchaseLink: '',
    supplier: '',
    amount: '',
    price: '',
    total: '',
    usage: ''
  }],
  // 试剂申请
  reagentProjects: [{
    category: '小组药品',
    image: '',
    name: '',
    cas: '',
    catalogNumber: '',
    vendor: '',
    specification: '',
    unit: '',
    purchaseChannel: '',
    amount: '',
    price: '',
    total: '',
    usage: ''
  }],
  // 请假申请
  leaveStart: '',
  leaveEnd: '',
  // 测试申请
  testPlatform: '',
  testProjects: [{
    project: '',
    unitPrice: '',
    quantity: '',
    totalAmount: ''
  }],
  testTotalAmount: '',
  // 出差申请
  destination: '',
  tripStartDate: '',
  tripEndDate: '',
  tripDays: '',
  tripPurpose: '',
  tripBudget: '',
  financeClaimAmount: '',
  financeCurrency: 'CNY',
  financeExpenseType: '',
  financeAttachments: [] as Array<{ name: string; url: string }>,
  selectedTeacherApprovers: [] as Array<{ id: string; name: string }>,
  selectedFinanceApprovers: [] as Array<{ id: string; name: string }>
});

type RecentSubmissionRow = {
  key: string;
  title: string;
  meta: string;
  status: ApprovalApplication['status'];
};

const APPLICATION_CARDS: Array<{
  category: ApprovalApplication['category'];
  title: string;
  description: string;
  icon: string;
}> = [
  {
    category: '耗材申请',
    title: '耗材申请',
    description: '从库房申领实验与办公耗材，快速补充日常实验所需。',
    icon: 'inventory_2'
  },
  {
    category: '试剂申请',
    title: '试剂申请',
    description: '订购化学与生物试剂，支持温控与特殊物流说明。',
    icon: 'science'
  },
  {
    category: '用章申请',
    title: '用章申请',
    description: '办理实验室公章、合同章等正式用印与证明材料。',
    icon: 'approval'
  },
  {
    category: '物品报修',
    title: '物品报修',
    description: '提交仪器与实验设施故障报修与维护工单。',
    icon: 'build'
  },
  {
    category: '请假',
    title: '请假申请',
    description: '申请事假、病假或科研调配等离岗与休假时间。',
    icon: 'calendar_today'
  },
  {
    category: '测试申请',
    title: '测试申请',
    description: '预约样品检测与分析服务，配置项目与费用。',
    icon: 'biotech'
  },
  {
    category: '出差申请',
    title: '出差申请',
    description: '学术会议、现场考察或外派交流的行程与预算。',
    icon: 'flight_takeoff'
  },
  {
    category: '财务报销',
    title: '财务报销',
    description: '提交差旅、办公等报销说明并上传发票或 PDF 等凭证。',
    icon: 'receipt_long'
  }
];

const ApplicationSubmission: React.FC = () => {
  const DEFAULT_REAGENT_CATEGORIES = ['公共药品', '小组药品'] as const;
  const DEFAULT_CONSUMABLE_CATEGORIES = ['公共耗材', '小组耗材'] as const;
  const navigate = useNavigate();
  const location = useLocation();
  const { isDemoBrowseOnly } = useAuth();
  // 申请表单相关状态
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedApplicationType, setSelectedApplicationType] = useState<ApprovalApplication['category'] | ''>('');
  /** 耗材/试剂：内部申请 vs 商家申请 */
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [showMerchantPickModal, setShowMerchantPickModal] = useState(false);
  const [pendingCategory, setPendingCategory] = useState<ApprovalApplication['category'] | ''>('');
  const [applyChannel, setApplyChannel] = useState<'internal' | 'merchant'>('internal');
  const [selectedMerchantId, setSelectedMerchantId] = useState('');
  const [selectedMerchantName, setSelectedMerchantName] = useState('');
  const [authorizedMerchants, setAuthorizedMerchants] = useState<Array<{ _id: string; name: string }>>([]);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState<SavedShippingAddress[]>([]);
  const [selectedSavedAddressId, setSelectedSavedAddressId] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [saveShippingAddress, setSaveShippingAddress] = useState(true);
  const [setDefaultShippingAddress, setSetDefaultShippingAddress] = useState(false);
  /** 低库存「去采购」关联的原提醒申请 ID */
  const [sourceSuggestionApplicationId, setSourceSuggestionApplicationId] = useState('');
  
  const [recentSubmissions, setRecentSubmissions] = useState<RecentSubmissionRow[]>([]);
  const [feedbackModal, setFeedbackModal] = useState<{ title: string; message: string } | null>(null);
  const [reasonImagesUploading, setReasonImagesUploading] = useState(false);
  const [teamTeachers, setTeamTeachers] = useState<TeamTeacherOption[]>([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamTeacherOption[]>([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);

  const showFeedback = (message: string, title = '提示') => {
    setFeedbackModal({ title, message });
  };

  const toRecentStatus = (status: string): ApprovalApplication['status'] => {
    if (status === '已通过' || status === '已驳回' || status === '已撤回') return status;
    return '待审批';
  };

  const loadRecentSubmissions = async () => {
    if (isDemoBrowseOnly) {
      setRecentSubmissions(getDemoRecentSubmissions());
      return;
    }
    try {
      const response = await getMyApprovalApplications();
      if (!response.success || !Array.isArray(response.data)) {
        setRecentSubmissions([]);
        return;
      }
      const rows: RecentSubmissionRow[] = response.data
        .slice(0, 3)
        .map((item: any) => {
          const status = toRecentStatus(String(item.status || '待审批'));
          const dateText = new Date(item.applyTime || item.appliedAt || item.createdAt || Date.now())
            .toLocaleDateString('zh-CN');
          return {
            key: String(item._id || item.key || `${Date.now()}-${Math.random()}`),
            title: `${item.category || '申请'} · ${item.title || '未命名申请'}`,
            meta: `${item.status || '待审批'} · ${dateText}`,
            status
          };
        });
      setRecentSubmissions(rows);
    } catch {
      setRecentSubmissions([]);
    }
  };

  useEffect(() => {
    void loadRecentSubmissions();
  }, [isDemoBrowseOnly]);

  const uploadApprovalImage = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const response = await fetch('/api/upload', {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: formData
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.url) {
      throw new Error(data?.error || '图片上传失败');
    }
    return data.url as string;
  };

  const handleReasonImageFiles = async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = e.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (!files.length) return;

    setReasonImagesUploading(true);
    try {
      for (const file of files) {
        try {
          const url = await uploadApprovalImage(file);
          setApplicationForm((prev) => ({
            ...prev,
            reasonImages: [...prev.reasonImages, { name: file.name, url }]
          }));
        } catch (error: any) {
          showFeedback(`${file.name}：${error?.message || '图片上传失败，请重试。'}`, '上传失败');
        }
      }
    } finally {
      setReasonImagesUploading(false);
      input.value = '';
    }
  };

  const removeReasonImage = (index: number) => {
    setApplicationForm((prev) => ({
      ...prev,
      reasonImages: prev.reasonImages.filter((_, i) => i !== index)
    }));
  };

  const handleFinanceAttachmentFiles = async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = e.target as HTMLInputElement;
    const files = input.files;
    if (!files?.length) return;
    for (const file of Array.from(files)) {
      const res = await uploadApprovalAttachmentFile(file);
      if (!res.success || !res.data?.url) {
        showFeedback(res.error || '凭证上传失败');
        continue;
      }
      setApplicationForm((prev) => ({
        ...prev,
        financeAttachments: [...prev.financeAttachments, { name: file.name, url: res.data!.url }]
      }));
    }
    input.value = '';
  };

  const removeFinanceAttachment = (index: number) => {
    setApplicationForm({
      ...applicationForm,
      financeAttachments: applicationForm.financeAttachments.filter((_, i) => i !== index)
    });
  };

  const [applicationForm, setApplicationForm] = useState({
    // 通用
    title: '',
    reason: '',
    reasonImages: [] as Array<{ name: string; url: string }>,
    amount: '',
    priority: '普通' as ApprovalApplication['priority'],
    // 耗材申请
    consumableProjects: [{
      category: '小组耗材',
      image: '',
      name: '',
      model: '',
      brand: '',
      unit: '',
      purchaseChannel: '网购' as '网购' | '其他',
      purchaseLink: '',
      supplier: '',
      amount: '',
      price: '',
      total: '',
      usage: ''
    }],
    // 试剂申请
    reagentProjects: [{
      category: '小组药品',
      image: '',
      name: '',
      cas: '',
      catalogNumber: '',
      vendor: '',
      specification: '',
      unit: '',
      purchaseChannel: '',
      amount: '',
      price: '',
      total: '',
      usage: ''
    }],
    // 请假申请
    leaveStart: '',
    leaveEnd: '',
    // 测试申请
    testPlatform: '',
    testProjects: [{
      project: '',
      unitPrice: '',
      quantity: '',
      totalAmount: ''
    }],
    testTotalAmount: '',
    // 出差申请
    destination: '',
    tripStartDate: '',
    tripEndDate: '',
    tripDays: '',
    tripPurpose: '',
    tripBudget: '',
    financeClaimAmount: '',
    financeCurrency: 'CNY',
    financeExpenseType: '',
    financeAttachments: [] as Array<{ name: string; url: string }>,
    selectedTeacherApprovers: [] as Array<{ id: string; name: string }>,
    selectedFinanceApprovers: [] as Array<{ id: string; name: string }>
  });

  const loadTeamTeachers = async () => {
    if (isDemoBrowseOnly) {
      setTeamTeachers([]);
      return;
    }
    setLoadingTeachers(true);
    try {
      const res = await getMembers();
      if (!res.success || !Array.isArray(res.data)) {
        setTeamTeachers([]);
        return;
      }
      const teachers = res.data
        .filter((m) => m.studentType === '教师' && !m.isDeleted)
        .map((m) => ({
          id: String(m._id || m.id || ''),
          name: m.name || m.username || '未命名教师'
        }))
        .filter((t) => t.id)
        .sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
      setTeamTeachers(teachers);
    } catch {
      setTeamTeachers([]);
    } finally {
      setLoadingTeachers(false);
    }
  };

  const toggleTeacherApprover = (teacher: TeamTeacherOption) => {
    setApplicationForm((prev) => {
      const exists = prev.selectedTeacherApprovers.some((t) => t.id === teacher.id);
      return {
        ...prev,
        selectedTeacherApprovers: exists
          ? prev.selectedTeacherApprovers.filter((t) => t.id !== teacher.id)
          : [...prev.selectedTeacherApprovers, { id: teacher.id, name: teacher.name }]
      };
    });
  };

  const loadTeamMembers = async () => {
    if (isDemoBrowseOnly) {
      setTeamMembers([]);
      return;
    }
    setLoadingTeamMembers(true);
    try {
      const res = await getMembers();
      if (!res.success || !Array.isArray(res.data)) {
        setTeamMembers([]);
        return;
      }
      const members = res.data
        .filter((m) => !m.isDeleted && m.role !== '超级管理员')
        .map((m) => ({
          id: String(m._id || m.id || ''),
          name: m.name || m.username || '未命名成员'
        }))
        .filter((t) => t.id)
        .sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
      setTeamMembers(members);
    } catch {
      setTeamMembers([]);
    } finally {
      setLoadingTeamMembers(false);
    }
  };

  const toggleFinanceApprover = (member: TeamTeacherOption) => {
    setApplicationForm((prev) => {
      const exists = prev.selectedFinanceApprovers.some((t) => t.id === member.id);
      return {
        ...prev,
        selectedFinanceApprovers: exists
          ? prev.selectedFinanceApprovers.filter((t) => t.id !== member.id)
          : [...prev.selectedFinanceApprovers, { id: member.id, name: member.name }]
      };
    });
  };

  const currentMaxUnitPriceForTeacherRule = (() => {
    if (selectedApplicationType !== '耗材申请' && selectedApplicationType !== '试剂申请') {
      return 0;
    }
    const calc = ApprovalFlowService.calculateAmounts({
      consumableProjects: applicationForm.consumableProjects,
      reagentProjects: applicationForm.reagentProjects
    } as ApprovalApplicationExtended);
    return calc.maxUnitPrice;
  })();
  const teacherApproverRequired =
    (selectedApplicationType === '耗材申请' || selectedApplicationType === '试剂申请') &&
    currentMaxUnitPriceForTeacherRule > TEACHER_APPROVER_UNIT_PRICE_THRESHOLD;

  const handleStartApplication = (type: ApprovalApplication['category']) => {
    if (type === '耗材申请' || type === '试剂申请') {
      setPendingCategory(type);
      setApplyChannel('internal');
      setSelectedMerchantId('');
      setSelectedMerchantName('');
      setShowChannelModal(true);
      return;
    }
    setApplyChannel('internal');
    setSelectedMerchantId('');
    setSelectedMerchantName('');
    setSelectedApplicationType(type);
    setShowApplicationModal(true);
    setApplicationForm(resetApplicationForm());
    if (type === '财务报销') {
      void loadTeamMembers();
    }
  };

  const loadShippingAddresses = async () => {
    try {
      const res = await getShippingAddresses();
      if (!res.success || !res.data) {
        setSavedAddresses([]);
        return;
      }
      const list = res.data.addresses || [];
      setSavedAddresses(list);
      const def = list.find((a) => a.isDefault) || list[0];
      if (def) {
        setSelectedSavedAddressId(def._id);
        setRecipientName(def.recipientName || '');
        setRecipientPhone(def.recipientPhone || '');
        setShippingAddress(def.address || '');
        setSetDefaultShippingAddress(Boolean(def.isDefault));
      } else {
        setSelectedSavedAddressId('');
        setRecipientName(res.data.profileDefaults?.recipientName || '');
        setRecipientPhone(res.data.profileDefaults?.recipientPhone || '');
        setShippingAddress('');
        setSetDefaultShippingAddress(false);
      }
    } catch {
      setSavedAddresses([]);
    }
  };

  const applySavedAddress = (id: string) => {
    setSelectedSavedAddressId(id);
    if (!id) return;
    const found = savedAddresses.find((a) => a._id === id);
    if (!found) return;
    setRecipientName(found.recipientName || '');
    setRecipientPhone(found.recipientPhone || '');
    setShippingAddress(found.address || '');
    setSetDefaultShippingAddress(Boolean(found.isDefault));
  };

  const openFormAfterChannel = (
    channel: 'internal' | 'merchant',
    merchantId = '',
    merchantName = ''
  ) => {
    if (!pendingCategory) return;
    setApplyChannel(channel);
    setSelectedMerchantId(merchantId);
    setSelectedMerchantName(merchantName);
    setSelectedApplicationType(pendingCategory);
    setShowChannelModal(false);
    setShowMerchantPickModal(false);
    setShowApplicationModal(true);
    setApplicationForm(resetApplicationForm());
    setSaveShippingAddress(true);
    void loadTeamTeachers();
    if (channel === 'merchant') {
      void loadShippingAddresses();
    } else {
      setSavedAddresses([]);
      setSelectedSavedAddressId('');
      setRecipientName('');
      setRecipientPhone('');
      setShippingAddress('');
      setSetDefaultShippingAddress(false);
    }
  };

  /** 低库存去采购：带预填直接打开申请单（内部或已选商家） */
  const openPrefillFromProcurement = (prefill: ProcurementPrefillState) => {
    const form = resetApplicationForm();
    form.title = prefill.title || form.title;
    form.reason = prefill.reason || form.reason;
    form.priority = prefill.priority || form.priority;
    if (prefill.category === '试剂申请' && prefill.reagentProject) {
      const rp = prefill.reagentProject;
      form.reagentProjects = [
        {
          category: rp.category === '公共药品' ? '公共药品' : '小组药品',
          image: rp.image || '',
          name: rp.name || '',
          cas: rp.cas || '',
          catalogNumber: rp.catalogNumber || '',
          vendor: rp.vendor || '',
          specification: rp.specification || '',
          unit: rp.unit || '',
          purchaseChannel: rp.purchaseChannel && rp.purchaseChannel !== '待定' ? rp.purchaseChannel : '网购',
          amount: rp.amount || '',
          price: rp.price || '',
          total: rp.total || '',
          usage: rp.usage || ''
        }
      ];
    }
    if (prefill.category === '耗材申请' && prefill.consumableProject) {
      const cp = prefill.consumableProject;
      form.consumableProjects = [
        {
          category: cp.category === '公共耗材' ? '公共耗材' : '小组耗材',
          image: cp.image || '',
          name: cp.name || '',
          model: cp.model || '',
          brand: cp.brand || '',
          unit: cp.unit || '',
          purchaseChannel: cp.purchaseChannel === '其他' ? '其他' : '网购',
          purchaseLink: cp.purchaseLink || '',
          supplier: cp.supplier || '',
          amount: cp.amount || '',
          price: cp.price || '',
          total: cp.total || '',
          usage: cp.usage || ''
        }
      ];
    }
    setSourceSuggestionApplicationId(prefill.sourceSuggestionApplicationId);
    setPendingCategory(prefill.category);
    setSelectedApplicationType(prefill.category);
    setApplyChannel(prefill.channel);
    setSelectedMerchantId(prefill.merchantId || '');
    setSelectedMerchantName(prefill.merchantName || '');
    setShowChannelModal(false);
    setShowMerchantPickModal(false);
    setShowApplicationModal(true);
    setApplicationForm(form);
    setSaveShippingAddress(true);
    void loadTeamTeachers();
    if (prefill.channel === 'merchant') {
      void loadShippingAddresses();
    } else {
      setSavedAddresses([]);
      setSelectedSavedAddressId('');
      setRecipientName('');
      setRecipientPhone('');
      setShippingAddress('');
      setSetDefaultShippingAddress(false);
    }
  };

  useEffect(() => {
    const state = location.state as Record<string, unknown> | null;
    const prefill = state?.[PROCUREMENT_PREFILL_STATE_KEY] as ProcurementPrefillState | undefined;
    if (!prefill?.category || !prefill.sourceSuggestionApplicationId) return;
    openPrefillFromProcurement(prefill);
    navigate(location.pathname, { replace: true, state: {} });
    // 仅在带预填进入时执行一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChooseInternal = () => {
    openFormAfterChannel('internal');
  };

  const handleChooseMerchantChannel = async () => {
    setLoadingMerchants(true);
    try {
      const res = await getAuthorizedMerchants();
      if (!res.success || !Array.isArray(res.data) || res.data.length === 0) {
        showFeedback('当前团队暂无已授权商家，请联系平台管理员完成商家授权后再试。');
        setLoadingMerchants(false);
        return;
      }
      setAuthorizedMerchants(res.data);
      setShowChannelModal(false);
      setShowMerchantPickModal(true);
    } catch {
      showFeedback('获取商家列表失败，请稍后重试。');
    } finally {
      setLoadingMerchants(false);
    }
  };

  // 计算测试申请总金额（与耗材/试剂一致：按「分」取整，避免浮点误差）
  const calculateTestTotal = (unitPrice: string, quantity: string) => {
    const line = lineTotalYuanFromStrings(unitPrice, quantity);
    return line === '' ? '0.00' : line;
  };

  // 计算所有测试项目的总金额
  const calculateAllTestTotal = (testProjects: Array<{project: string, unitPrice: string, quantity: string, totalAmount: string}>) => {
    return testProjects.reduce((total, project) => {
      return total + (parseFloat(project.totalAmount) || 0);
    }, 0).toFixed(2);
  };

  // 处理测试申请字段变化
  const handleTestFieldChange = (field: string, value: string) => {
    const newForm = { ...applicationForm, [field]: value };
    
    // 如果是出差日期变化，自动计算出差天数
    if (field === 'tripStartDate' || field === 'tripEndDate') {
      const startDate = field === 'tripStartDate' ? value : applicationForm.tripStartDate;
      const endDate = field === 'tripEndDate' ? value : applicationForm.tripEndDate;
      
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 包含出发和返回当天
        newForm.tripDays = diffDays.toString();
      } else {
        newForm.tripDays = '';
      }
    }
    
    setApplicationForm(newForm);
  };

  // 处理测试项目字段变化
  const handleTestProjectChange = (index: number, field: string, value: string) => {
    const newTestProjects = [...applicationForm.testProjects];
    newTestProjects[index] = { ...newTestProjects[index], [field]: value };
    
    // 如果是单价或数量变化，自动计算该项目总金额
    if (field === 'unitPrice' || field === 'quantity') {
      const unitPrice = field === 'unitPrice' ? value : newTestProjects[index].unitPrice;
      const quantity = field === 'quantity' ? value : newTestProjects[index].quantity;
      newTestProjects[index].totalAmount = calculateTestTotal(unitPrice, quantity);
    }
    
    // 计算所有项目的总金额
    const totalAmount = calculateAllTestTotal(newTestProjects);
    
    setApplicationForm({
      ...applicationForm,
      testProjects: newTestProjects,
      testTotalAmount: totalAmount
    });
  };

  // 添加测试项目
  const addTestProject = () => {
    const newTestProjects = [...applicationForm.testProjects, {
      project: '',
      unitPrice: '',
      quantity: '',
      totalAmount: ''
    }];
    setApplicationForm({
      ...applicationForm,
      testProjects: newTestProjects
    });
  };

  // 删除测试项目
  const removeTestProject = (index: number) => {
    if (applicationForm.testProjects.length > 1) {
      const newTestProjects = applicationForm.testProjects.filter((_, i) => i !== index);
      const totalAmount = calculateAllTestTotal(newTestProjects);
      setApplicationForm({
        ...applicationForm,
        testProjects: newTestProjects,
        testTotalAmount: totalAmount
      });
    }
  };

  // 添加耗材项目
  const addConsumableProject = () => {
    const newConsumableProjects = [...applicationForm.consumableProjects, {
      category: '小组耗材',
      image: '',
      name: '',
      model: '',
      brand: '',
      unit: '',
      purchaseChannel: '网购' as '网购' | '其他',
      purchaseLink: '',
      supplier: '',
      amount: '',
      price: '',
      total: '',
      usage: ''
    }];
    setApplicationForm({
      ...applicationForm,
      consumableProjects: newConsumableProjects
    });
  };

  // 删除耗材项目
  const removeConsumableProject = (index: number) => {
    if (applicationForm.consumableProjects.length > 1) {
      const newConsumableProjects = applicationForm.consumableProjects.filter((_, i) => i !== index);
      setApplicationForm({
        ...applicationForm,
        consumableProjects: newConsumableProjects
      });
    }
  };

  // 处理耗材项目字段变化
  const handleConsumableProjectChange = (index: number, field: string, value: string) => {
    const newConsumableProjects = [...applicationForm.consumableProjects];
    newConsumableProjects[index] = {
      ...newConsumableProjects[index],
      [field]: value
    };
    
    // 如果是价格或数量变化，重新计算总价（分整数运算，避免 number 输入框浮点误差）
    if (field === 'price' || field === 'amount') {
      newConsumableProjects[index].total = lineTotalYuanFromStrings(
        newConsumableProjects[index].price,
        newConsumableProjects[index].amount
      );
    }
    
    setApplicationForm({
      ...applicationForm,
      consumableProjects: newConsumableProjects
    });
  };

  // 添加试剂项目
  const addReagentProject = () => {
    const newReagentProjects = [...applicationForm.reagentProjects, {
      category: '小组药品',
      image: '',
      name: '',
      cas: '',
      catalogNumber: '',
      vendor: '',
      specification: '',
      unit: '',
      purchaseChannel: '',
      amount: '',
      price: '',
      total: '',
      usage: ''
    }];
    setApplicationForm({
      ...applicationForm,
      reagentProjects: newReagentProjects
    });
  };

  // 删除试剂项目
  const removeReagentProject = (index: number) => {
    if (applicationForm.reagentProjects.length > 1) {
      const newReagentProjects = applicationForm.reagentProjects.filter((_, i) => i !== index);
      setApplicationForm({
        ...applicationForm,
        reagentProjects: newReagentProjects
      });
    }
  };

  // 处理试剂项目字段变化
  const handleReagentProjectChange = (index: number, field: string, value: string) => {
    const newReagentProjects = [...applicationForm.reagentProjects];
    newReagentProjects[index] = {
      ...newReagentProjects[index],
      [field]: value
    };
    
    // 如果是价格或数量变化，重新计算总价
    if (field === 'price' || field === 'amount') {
      newReagentProjects[index].total = lineTotalYuanFromStrings(
        newReagentProjects[index].price,
        newReagentProjects[index].amount
      );
    }
    
    setApplicationForm({
      ...applicationForm,
      reagentProjects: newReagentProjects
    });
  };

  const reagentCategoryOptions = [...DEFAULT_REAGENT_CATEGORIES];
  const consumableCategoryOptions = [...DEFAULT_CONSUMABLE_CATEGORIES];

  const handleSubmitApplication = () => {
    const submit = async () => {
    if (!applicationForm.title || !applicationForm.reason) {
      showFeedback('请填写申请标题与申请原因等必填项。');
      return;
    }

    // 测试申请额外验证
    if (selectedApplicationType === '测试申请') {
      if (!applicationForm.testPlatform) {
        showFeedback('请填写测试平台。');
        return;
      }
      
      // 检查所有测试项目是否填写完整
      for (let i = 0; i < applicationForm.testProjects.length; i++) {
        const project = applicationForm.testProjects[i];
        if (!project.project || !project.unitPrice || !project.quantity) {
          showFeedback(`请填写第 ${i + 1} 个测试项目的所有必填项。`);
          return;
        }
      }
    }

    // 试剂申请额外验证
    if (selectedApplicationType === '试剂申请') {
      // 检查所有试剂项目是否填写完整
      for (let i = 0; i < applicationForm.reagentProjects.length; i++) {
        const project = applicationForm.reagentProjects[i];
        if (!project.category || !project.cas || !project.vendor || !project.specification ||
            !project.unit || !project.purchaseChannel || !project.amount) {
          showFeedback(`请填写第 ${i + 1} 个试剂项目的所有必填项。`);
          return;
        }
      }
    }

    // 耗材/试剂：单价超过阈值时必须勾选教师审批人
    if (selectedApplicationType === '耗材申请' || selectedApplicationType === '试剂申请') {
      const priceCheck = ApprovalFlowService.calculateAmounts({
        consumableProjects: applicationForm.consumableProjects,
        reagentProjects: applicationForm.reagentProjects
      } as ApprovalApplicationExtended);
      if (
        priceCheck.maxUnitPrice > TEACHER_APPROVER_UNIT_PRICE_THRESHOLD &&
        applicationForm.selectedTeacherApprovers.length === 0
      ) {
        showFeedback(
          `申请中存在单价超过 ${TEACHER_APPROVER_UNIT_PRICE_THRESHOLD} 元的明细，请至少勾选 1 名教师审批人。`
        );
        return;
      }
    }

    // 出差申请额外验证
    if (selectedApplicationType === '出差申请') {
      if (!applicationForm.destination || !applicationForm.tripStartDate || 
          !applicationForm.tripEndDate || !applicationForm.tripPurpose) {
        showFeedback('请填写出差申请的全部必填项（目的地、起止日期、事由）。');
        return;
      }
      
      // 验证日期逻辑
      if (applicationForm.tripStartDate && applicationForm.tripEndDate) {
        const startDate = new Date(applicationForm.tripStartDate);
        const endDate = new Date(applicationForm.tripEndDate);
        if (startDate > endDate) {
          showFeedback('出发日期不能晚于返回日期。');
          return;
        }
      }
    }

    // 请假申请额外验证（与后端 applicationData 字段一致，供成员请假 / 值日冲突查询）
    if (selectedApplicationType === '请假') {
      if (!applicationForm.leaveStart || !applicationForm.leaveEnd) {
        showFeedback('请选择请假开始与结束日期。');
        return;
      }
      const leaveStartAt = new Date(applicationForm.leaveStart);
      const leaveEndAt = new Date(applicationForm.leaveEnd);
      if (leaveStartAt > leaveEndAt) {
        showFeedback('请假结束日期不能早于开始日期。');
        return;
      }
    }

    if (selectedApplicationType === '财务报销') {
      const claim = parseFloat(String(applicationForm.financeClaimAmount || '').trim());
      if (!Number.isFinite(claim) || claim <= 0) {
        showFeedback('请填写有效的报销金额。');
        return;
      }
      if (!applicationForm.financeAttachments.length) {
        showFeedback('请至少上传一份报销凭证（PDF 或图片）。');
        return;
      }
    }

    if (!selectedApplicationType) {
      showFeedback('请选择申请类型后重试。');
      return;
    }

    // 计算金额信息
    let maxUnitPrice = 0;
    let totalAmount = 0;
    if (selectedApplicationType === '财务报销') {
      totalAmount = parseFloat(String(applicationForm.financeClaimAmount || '').trim()) || 0;
      maxUnitPrice = totalAmount;
    } else {
      const calc = ApprovalFlowService.calculateAmounts({
        consumableProjects: applicationForm.consumableProjects,
        reagentProjects: applicationForm.reagentProjects,
        testProjects: applicationForm.testProjects
      } as ApprovalApplicationExtended);
      maxUnitPrice = calc.maxUnitPrice;
      totalAmount = calc.totalAmount;
    }

    const basePayload = {
      title: applicationForm.title,
      category: selectedApplicationType,
      reason: applicationForm.reason,
      reasonImages: applicationForm.reasonImages,
      priority: applicationForm.priority,
      maxUnitPrice,
      totalAmount,
      selectedTeacherApprovers:
        selectedApplicationType === '耗材申请' || selectedApplicationType === '试剂申请'
          ? applicationForm.selectedTeacherApprovers
          : [],
      selectedFinanceApprovers:
        selectedApplicationType === '财务报销' ? applicationForm.selectedFinanceApprovers : []
    };

    const typePayload: Record<string, unknown> = { ...basePayload };
    if (
      (selectedApplicationType === '耗材申请' || selectedApplicationType === '试剂申请') &&
      applyChannel === 'merchant'
    ) {
      if (!selectedMerchantId) {
        showFeedback('请先选择商家。');
        return;
      }
      typePayload.applyChannel = 'merchant';
      typePayload.merchantId = selectedMerchantId;
      typePayload.merchantName = selectedMerchantName;
      if (!recipientName.trim()) {
        showFeedback('请填写收件人姓名。');
        return;
      }
      if (!recipientPhone.trim()) {
        showFeedback('请填写收件人手机号。');
        return;
      }
      if (!shippingAddress.trim()) {
        showFeedback('请填写收货地址。');
        return;
      }
      typePayload.recipientName = recipientName.trim();
      typePayload.recipientPhone = recipientPhone.trim();
      typePayload.shippingAddress = shippingAddress.trim();
      typePayload.saveShippingAddress = saveShippingAddress;
      typePayload.setDefaultShippingAddress = setDefaultShippingAddress;
    } else if (selectedApplicationType === '耗材申请' || selectedApplicationType === '试剂申请') {
      typePayload.applyChannel = 'internal';
    }

    if (
      sourceSuggestionApplicationId &&
      (selectedApplicationType === '耗材申请' || selectedApplicationType === '试剂申请')
    ) {
      typePayload.sourceSuggestionApplicationId = sourceSuggestionApplicationId;
    }

    switch (selectedApplicationType) {
      case '耗材申请':
        typePayload.consumableProjects = applicationForm.consumableProjects;
        break;
      case '试剂申请':
        typePayload.reagentProjects = applicationForm.reagentProjects;
        break;
      case '请假':
        typePayload.leaveStart = applicationForm.leaveStart;
        typePayload.leaveEnd = applicationForm.leaveEnd;
        break;
      case '出差申请':
        typePayload.destination = applicationForm.destination;
        typePayload.tripStartDate = applicationForm.tripStartDate;
        typePayload.tripEndDate = applicationForm.tripEndDate;
        typePayload.tripDays = applicationForm.tripDays;
        typePayload.tripPurpose = applicationForm.tripPurpose;
        typePayload.tripBudget = applicationForm.tripBudget;
        break;
      case '测试申请':
        typePayload.testPlatform = applicationForm.testPlatform;
        typePayload.testProjects = applicationForm.testProjects;
        typePayload.testTotalAmount = applicationForm.testTotalAmount;
        break;
      case '财务报销':
        typePayload.financeReimbursement = {
          claimAmount: totalAmount,
          currency: applicationForm.financeCurrency || 'CNY',
          expenseType: applicationForm.financeExpenseType || '',
          remark: applicationForm.reason,
          attachments: applicationForm.financeAttachments
        };
        break;
      default:
        break;
    }

    try {
      const response = await submitApprovalApplication(typePayload);
      if (!response.success || !response.data) {
        showFeedback(response.error || '申请提交失败，请稍后重试。', '提交失败');
        return;
      }

      await loadRecentSubmissions();
      const showAmountDetail =
        totalAmount > 0 &&
        ['耗材申请', '试剂申请', '测试申请', '财务报销'].includes(selectedApplicationType);
      const successMessage = showAmountDetail
        ? `申请提交成功。\n\n申请金额：¥${totalAmount.toFixed(2)}\n最大单价：¥${maxUnitPrice.toFixed(
            2
          )}\n\n审批流程已启动，请等待审批结果。`
        : '申请提交成功。\n\n审批流程已启动，请等待审批结果。';
      showFeedback(successMessage, '提交成功');
      setShowApplicationModal(false);
      setSelectedApplicationType('');
      setPendingCategory('');
      setSourceSuggestionApplicationId('');
      setApplyChannel('internal');
      setSelectedMerchantId('');
      setSelectedMerchantName('');
      setApplicationForm(resetApplicationForm());
    } catch (error) {
      showFeedback('网络异常，提交失败，请稍后重试。', '提交失败');
    }
    };
    submit();
  };

  const recentStatusPresentation = (status: ApprovalApplication['status']) => {
    switch (status) {
      case '已通过':
        return {
          pill: 'as-recent-pill as-recent-pill--ok',
          label: '已通过',
          iconWrap: 'as-recent-icon as-recent-icon--ok',
          icon: 'check_circle' as const,
          iconFill: true
        };
      case '待审批':
        return {
          pill: 'as-recent-pill as-recent-pill--progress',
          label: '处理中',
          iconWrap: 'as-recent-icon as-recent-icon--progress',
          icon: 'sync' as const,
          iconFill: false
        };
      case '已驳回':
        return {
          pill: 'as-recent-pill as-recent-pill--fail',
          label: '已驳回',
          iconWrap: 'as-recent-icon as-recent-icon--fail',
          icon: 'cancel' as const,
          iconFill: false
        };
      case '已撤回':
        return {
          pill: 'as-recent-pill as-recent-pill--draft',
          label: '已撤回',
          iconWrap: 'as-recent-icon as-recent-icon--draft',
          icon: 'undo' as const,
          iconFill: false
        };
    }
  };

  return (
    <div className="page-container full-height members-lims app-submission-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>工作台</span>
        <span className="mm-crumb-sep">/</span>
        <span>发起申请</span>
      </nav>

      <header className="mm-page-head">
        <div>
          <h1 className="mm-title">发起申请</h1>
          <p className="mm-subtitle mb-0">
            使用统一入口快速发起实验室各类审批请求，并在下方查看最近三条动态。
          </p>
        </div>
      </header>

      <div className="content-area px-0">
        <div className="as-request-grid">
          {APPLICATION_CARDS.map((item) => (
            <article key={item.category} className="as-request-card">
              <div>
                <div className="as-request-card-icon">
                  <LimsNavIcon name={item.icon} sizePx={30} className="as-request-card-icon-glyph" />
                </div>
                <h3 className="as-request-card-title">{item.title}</h3>
                <p className="as-request-card-desc">{item.description}</p>
              </div>
              <button type="button" className="as-request-card-cta" onClick={() => handleStartApplication(item.category)}>
                发起申请
                <LimsNavIcon name="add" sizePx={20} />
              </button>
            </article>
          ))}
        </div>

        <section className="as-recent-section" aria-labelledby="as-recent-heading">
          <div className="as-recent-head">
            <div>
              <h2 id="as-recent-heading" className="as-recent-title">
                最近提交
              </h2>
              <p className="as-recent-sub">最近三条申请的状态摘要；新提交会排在最前。</p>
            </div>
            <button type="button" className="as-recent-all" onClick={() => navigate('/workbench/approval-management')}>
              查看全部
            </button>
          </div>
          <div className="as-recent-list">
            {recentSubmissions.map((row) => {
              const vis = recentStatusPresentation(row.status);
              return (
                <div key={row.key} className="as-recent-row">
                  <div className="d-flex align-items-center gap-3 flex-grow-1 min-w-0">
                    <div className={vis.iconWrap}>
                      <span
                        className="material-symbols-outlined"
                        style={{
                          fontSize: 22,
                          ...(vis.iconFill ? { fontVariationSettings: "'FILL' 1" } : {})
                        }}
                        aria-hidden
                      >
                        {vis.icon}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="as-recent-row-title text-truncate">{row.title}</p>
                      <p className="as-recent-row-meta">{row.meta}</p>
                    </div>
                  </div>
                  <span className={vis.pill}>{vis.label}</span>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* 申请表单模态框 */}
      <Modal show={showApplicationModal} onHide={() => setShowApplicationModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedApplicationType &&
              `${selectedApplicationType}${
                applyChannel === 'merchant' && selectedMerchantName
                  ? ` · 商家申请（${selectedMerchantName}）`
                  : applyChannel === 'merchant'
                    ? ' · 商家申请'
                    : selectedApplicationType === '耗材申请' || selectedApplicationType === '试剂申请'
                      ? ' · 内部申请'
                      : ''
              }`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>申请标题 *</Form.Label>
              <Form.Control
                value={applicationForm.title}
                onChange={e => setApplicationForm({ ...applicationForm, title: e.target.value })}
                placeholder="请输入申请标题"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>申请原因 *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={applicationForm.reason}
                onChange={e => setApplicationForm({ ...applicationForm, reason: e.target.value })}
                placeholder="请详细说明申请原因"
                required
              />
              <div className="mt-2">
                <Form.Label className="small mb-1">原因图片（可选）</Form.Label>
                <Form.Control
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.webp,.heic,.heif,image/*"
                  multiple
                  disabled={reasonImagesUploading}
                  onChange={(e) => void handleReasonImageFiles(e)}
                />
                <Form.Text className="text-muted">
                  {reasonImagesUploading ? '图片上传中…' : '支持常见图片格式，单张不超过 10MB，可上传多张。'}
                </Form.Text>
                {applicationForm.reasonImages.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {applicationForm.reasonImages.map((image, index) => (
                      <div key={`${image.url}-${index}`} className="position-relative border rounded p-1">
                        <img
                          src={resolveUploadedImageUrl(image.url)}
                          alt={image.name || `原因图片 ${index + 1}`}
                          style={{ width: 88, height: 88, objectFit: 'cover', display: 'block' }}
                        />
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 py-0 px-1"
                          aria-label={`移除${image.name || `原因图片 ${index + 1}`}`}
                          onClick={() => removeReasonImage(index)}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Form.Group>

            {(selectedApplicationType === '耗材申请' || selectedApplicationType === '试剂申请') && (
              <Form.Group className="mb-3">
                <Form.Label>
                  教师审批人{teacherApproverRequired ? ' *' : '（可选）'}
                </Form.Label>
                <Form.Text className="d-block text-muted mb-2">
                  {teacherApproverRequired
                    ? `当前最大单价 ¥${currentMaxUnitPriceForTeacherRule.toFixed(2)}，超过 ${TEACHER_APPROVER_UNIT_PRICE_THRESHOLD} 元，须至少勾选 1 名教师。勾选后仅需教师审批（任一人通过即可），无需设置审批人再批。`
                    : `当前最大单价未超过 ${TEACHER_APPROVER_UNIT_PRICE_THRESHOLD} 元，教师审批人可选。勾选后仅需教师审批（任一人通过即可）；不勾选则由审批设置中的审批人审批。`}
                </Form.Text>
                {loadingTeachers ? (
                  <div className="text-muted small">正在加载教师列表…</div>
                ) : teamTeachers.length === 0 ? (
                  <div className="text-muted small">本团队暂无身份为「教师」的成员，请联系管理员维护成员身份。</div>
                ) : (
                  <div className="border rounded-3 p-2" style={{ maxHeight: 180, overflowY: 'auto' }}>
                    {teamTeachers.map((teacher) => {
                      const checked = applicationForm.selectedTeacherApprovers.some(
                        (t) => t.id === teacher.id
                      );
                      return (
                        <Form.Check
                          key={teacher.id}
                          type="checkbox"
                          id={`teacher-approver-${teacher.id}`}
                          className="mb-1"
                          label={teacher.name}
                          checked={checked}
                          onChange={() => toggleTeacherApprover(teacher)}
                        />
                      );
                    })}
                  </div>
                )}
              </Form.Group>
            )}

            {selectedApplicationType === '财务报销' && (
              <Form.Group className="mb-3">
                <Form.Label>报销审批人（可选）</Form.Label>
                <Form.Text className="d-block text-muted mb-2">
                  可勾选本团队任意成员。勾选后仅需被选中的人审批（任一人通过即可）；不勾选则由审批设置中的财务报销审批人审批。
                </Form.Text>
                {loadingTeamMembers ? (
                  <div className="text-muted small">正在加载成员列表…</div>
                ) : teamMembers.length === 0 ? (
                  <div className="text-muted small">本团队暂无可选成员。</div>
                ) : (
                  <div className="border rounded-3 p-2" style={{ maxHeight: 180, overflowY: 'auto' }}>
                    {teamMembers.map((member) => {
                      const checked = applicationForm.selectedFinanceApprovers.some(
                        (t) => t.id === member.id
                      );
                      return (
                        <Form.Check
                          key={member.id}
                          type="checkbox"
                          id={`finance-approver-${member.id}`}
                          className="mb-1"
                          label={member.name}
                          checked={checked}
                          onChange={() => toggleFinanceApprover(member)}
                        />
                      );
                    })}
                  </div>
                )}
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>优先级</Form.Label>
              <Form.Select
                value={applicationForm.priority}
                onChange={e => setApplicationForm({ ...applicationForm, priority: e.target.value as ApprovalApplication['priority'] })}
              >
                <option value="普通">普通</option>
                <option value="紧急">紧急</option>
                <option value="特急">特急</option>
              </Form.Select>
            </Form.Group>

            {applyChannel === 'merchant' &&
              (selectedApplicationType === '耗材申请' || selectedApplicationType === '试剂申请') && (
                <div className="border rounded-3 p-3 mb-3" style={{ background: 'rgba(0, 53, 95, 0.03)' }}>
                  <div className="fw-semibold mb-2">收货信息（商家发货）</div>
                  {savedAddresses.length > 0 && (
                    <Form.Group className="mb-3">
                      <Form.Label>常用地址</Form.Label>
                      <Form.Select
                        value={selectedSavedAddressId}
                        onChange={(e) => applySavedAddress(e.target.value)}
                      >
                        <option value="">手动填写 / 新建</option>
                        {savedAddresses.map((a) => (
                          <option key={a._id} value={a._id}>
                            {(a.isDefault ? '【默认】' : '') +
                              `${a.recipientName} · ${a.recipientPhone} · ${a.address}`}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  )}
                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label>收件人姓名 *</Form.Label>
                        <Form.Control
                          value={recipientName}
                          onChange={(e) => {
                            setRecipientName(e.target.value);
                            setSelectedSavedAddressId('');
                          }}
                          placeholder="请输入收件人姓名"
                          required
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label>收件人手机号 *</Form.Label>
                        <Form.Control
                          value={recipientPhone}
                          onChange={(e) => {
                            setRecipientPhone(e.target.value);
                            setSelectedSavedAddressId('');
                          }}
                          placeholder="请输入手机号"
                          required
                        />
                      </Form.Group>
                    </div>
                  </div>
                  <Form.Group className="mb-3">
                    <Form.Label>收货地址 *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={shippingAddress}
                      onChange={(e) => {
                        setShippingAddress(e.target.value);
                        setSelectedSavedAddressId('');
                      }}
                      placeholder="省市区 + 详细地址"
                      required
                    />
                  </Form.Group>
                  <Form.Check
                    type="checkbox"
                    id="save-shipping-address"
                    className="mb-2"
                    label="保存为常用收货地址"
                    checked={saveShippingAddress}
                    onChange={(e) => setSaveShippingAddress(e.target.checked)}
                  />
                  <Form.Check
                    type="checkbox"
                    id="default-shipping-address"
                    label="设为默认收货地址"
                    checked={setDefaultShippingAddress}
                    onChange={(e) => {
                      setSetDefaultShippingAddress(e.target.checked);
                      if (e.target.checked) setSaveShippingAddress(true);
                    }}
                  />
                </div>
              )}

            {selectedApplicationType === '财务报销' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>报销金额（元）*</Form.Label>
                  <Form.Control
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    value={applicationForm.financeClaimAmount}
                    onChange={(e) => setApplicationForm({ ...applicationForm, financeClaimAmount: e.target.value })}
                    placeholder="0.00"
                  />
                </Form.Group>
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>币种</Form.Label>
                      <Form.Select
                        value={applicationForm.financeCurrency}
                        onChange={(e) => setApplicationForm({ ...applicationForm, financeCurrency: e.target.value })}
                      >
                        <option value="CNY">人民币 CNY</option>
                        <option value="USD">美元 USD</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>费用类型</Form.Label>
                      <Form.Control
                        placeholder="如差旅、办公、会议注册费等"
                        value={applicationForm.financeExpenseType}
                        onChange={(e) => setApplicationForm({ ...applicationForm, financeExpenseType: e.target.value })}
                      />
                    </Form.Group>
                  </div>
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>报销凭证 *</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,application/pdf,image/*"
                    multiple
                    onChange={(e) => void handleFinanceAttachmentFiles(e)}
                  />
                  <Form.Text className="text-muted">支持 PDF 与常见图片，单文件不超过 20MB，可多次选择追加。</Form.Text>
                  {applicationForm.financeAttachments.length > 0 && (
                    <ul className="list-unstyled mb-0 mt-2 small">
                      {applicationForm.financeAttachments.map((att, i) => (
                        <li
                          key={`${att.url}-${i}`}
                          className="d-flex align-items-center justify-content-between gap-2 py-1 border-bottom"
                        >
                          <span className="text-truncate">{att.name}</span>
                          <Button variant="outline-danger" size="sm" type="button" onClick={() => removeFinanceAttachment(i)}>
                            移除
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Form.Group>
              </>
            )}

            {/* 请假申请专用字段 */}
            {selectedApplicationType === '请假' && (
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>请假开始日期 *</Form.Label>
                    <Form.Control
                      type="date"
                      value={applicationForm.leaveStart || ''}
                      onChange={(e) => handleTestFieldChange('leaveStart', e.target.value)}
                      required
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>请假结束日期 *</Form.Label>
                    <Form.Control
                      type="date"
                      value={applicationForm.leaveEnd || ''}
                      onChange={(e) => handleTestFieldChange('leaveEnd', e.target.value)}
                      required
                    />
                  </Form.Group>
                </div>
              </div>
            )}

            {/* 耗材申请专用字段 */}
            {selectedApplicationType === '耗材申请' && (
              <>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="mb-0">耗材项目 *</Form.Label>
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={addConsumableProject}
                    >
                      + 添加耗材
                    </Button>
                  </div>
                  
                  {applicationForm.consumableProjects.map((consumableProject, index) => (
                    <Card key={index} className="mb-3" style={{ border: '1px solid #dee2e6' }}>
                      <Card.Header className="py-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-bold">耗材项目 {index + 1}</span>
                          {applicationForm.consumableProjects.length > 1 && (
                            <Button 
                              variant="outline-danger" 
                              size="sm" 
                              onClick={() => removeConsumableProject(index)}
                            >
                              删除
                            </Button>
                          )}
                        </div>
                      </Card.Header>
                      <Card.Body className="py-3">
                        <Form.Group className="mb-3">
                          <Form.Label>耗材分类 *</Form.Label>
                          <Form.Select
                            value={consumableProject.category || '小组耗材'}
                            onChange={(e) => handleConsumableProjectChange(index, 'category', e.target.value)}
                            required
                          >
                            {consumableCategoryOptions.map((category) => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>耗材图片</Form.Label>
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const target = e.target as HTMLInputElement;
                              const file = target.files?.[0];
                              if (!file) return;
                              try {
                                const imageUrl = await uploadApprovalImage(file);
                                handleConsumableProjectChange(index, 'image', imageUrl);
                              } catch (error: any) {
                                showFeedback(error?.message || '图片上传失败，请重试。', '上传失败');
                              }
                            }}
                          />
                          <Form.Text className="text-muted">
                            上传耗材的图片（可选）
                          </Form.Text>
                        </Form.Group>
                        
                        <div className="row">
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>耗材名称 *</Form.Label>
                              <Form.Control
                                value={consumableProject.name}
                                onChange={e => handleConsumableProjectChange(index, 'name', e.target.value)}
                                placeholder="请输入耗材名称"
                                required
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>型号 *</Form.Label>
                              <Form.Control
                                value={consumableProject.model}
                                onChange={e => handleConsumableProjectChange(index, 'model', e.target.value)}
                                placeholder="请输入耗材型号"
                                required
                              />
                            </Form.Group>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>品牌</Form.Label>
                              <Form.Select
                                value={consumableProject.brand || ''}
                                onChange={e => handleConsumableProjectChange(index, 'brand', e.target.value)}
                              >
                                <option value="">请选择品牌</option>
                                {getConsumableBrandOptions(consumableProject.brand).map((brand) => (
                                  <option key={brand} value={brand}>
                                    {brand}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </div>
                        </div>
                        
                        <div className="row">
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>单位 *</Form.Label>
                              <Form.Control
                                value={consumableProject.unit}
                                onChange={e => handleConsumableProjectChange(index, 'unit', e.target.value)}
                                placeholder="请输入单位（如：个、盒、包等）"
                                required
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                            </Form.Group>
                          </div>
                        </div>
                        
                        <div className="row">
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>购买渠道 *</Form.Label>
                              <Form.Select
                                value={consumableProject.purchaseChannel}
                                onChange={e => handleConsumableProjectChange(index, 'purchaseChannel', e.target.value)}
                                required
                              >
                                <option value="网购">网购</option>
                                <option value="其他">其他</option>
                              </Form.Select>
                            </Form.Group>
                          </div>
                        </div>
                        
                        {consumableProject.purchaseChannel === '网购' && (
                          <Form.Group className="mb-3">
                            <Form.Label>购买链接 *</Form.Label>
                            <Form.Control
                              type="url"
                              value={consumableProject.purchaseLink || ''}
                              onChange={e => handleConsumableProjectChange(index, 'purchaseLink', e.target.value)}
                              placeholder="请输入购买链接"
                              required
                            />
                          </Form.Group>
                        )}
                        
                        {consumableProject.purchaseChannel === '其他' && (
                          <Form.Group className="mb-3">
                            <Form.Label>渠道商 *</Form.Label>
                            <Form.Control
                              value={consumableProject.supplier || ''}
                              onChange={e => handleConsumableProjectChange(index, 'supplier', e.target.value)}
                              placeholder="请输入渠道商名称"
                              required
                            />
                          </Form.Group>
                        )}
                        
                        <div className="row">
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>申请数量 *</Form.Label>
                              <Form.Control
                                type="text"
                                inputMode="numeric"
                                value={consumableProject.amount}
                                onChange={e => handleConsumableProjectChange(index, 'amount', e.target.value)}
                                onBlur={(e) => {
                                  const v = e.target.value.trim();
                                  if (v === '') return;
                                  const n = Math.max(1, Math.round(parseFloat(v) || 0));
                                  if (String(n) !== v) {
                                    handleConsumableProjectChange(index, 'amount', String(n));
                                  }
                                }}
                                placeholder="请输入申请数量（正整数）"
                                required
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>单价（元）</Form.Label>
                              <Form.Control
                                type="text"
                                inputMode="decimal"
                                value={consumableProject.price || ''}
                                onChange={e => handleConsumableProjectChange(index, 'price', e.target.value)}
                                onBlur={(e) => {
                                  const v = e.target.value.trim();
                                  if (v === '') return;
                                  const n = parseFloat(v.replace(/,/g, ''));
                                  if (!Number.isFinite(n) || n < 0) return;
                                  const fixed = (Math.round(n * 100) / 100).toFixed(2);
                                  if (fixed !== v) {
                                    handleConsumableProjectChange(index, 'price', fixed);
                                  }
                                }}
                                placeholder="请输入单价"
                              />
                            </Form.Group>
                          </div>
                        </div>
                        
                        {consumableProject.price && consumableProject.amount && (
                          <div className="row">
                            <div className="col-md-6">
                              <Form.Group className="mb-3">
                                <Form.Label>总价（元）</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={consumableProject.total || ''}
                                  readOnly
                                  className="bg-light"
                                />
                              </Form.Group>
                            </div>
                          </div>
                        )}
                        
                        <Form.Group className="mb-3">
                          <Form.Label>使用说明</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={consumableProject.usage || ''}
                            onChange={e => handleConsumableProjectChange(index, 'usage', e.target.value)}
                            placeholder="请说明耗材的使用目的和用途"
                          />
                        </Form.Group>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </>
            )}

            {/* 试剂申请专用字段 */}
            {selectedApplicationType === '试剂申请' && (
              <>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="mb-0">试剂项目 *</Form.Label>
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={addReagentProject}
                    >
                      + 添加试剂
                    </Button>
                  </div>
                  
                  {applicationForm.reagentProjects.map((reagentProject, index) => (
                    <Card key={index} className="mb-3" style={{ border: '1px solid #dee2e6' }}>
                      <Card.Header className="py-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-bold">试剂项目 {index + 1}</span>
                          {applicationForm.reagentProjects.length > 1 && (
                            <Button 
                              variant="outline-danger" 
                              size="sm" 
                              onClick={() => removeReagentProject(index)}
                            >
                              删除
                            </Button>
                          )}
                        </div>
                      </Card.Header>
                      <Card.Body className="py-3">
                        <Form.Group className="mb-3">
                          <Form.Label>试剂分类 *</Form.Label>
                          <Form.Select
                            value={reagentProject.category || '小组药品'}
                            onChange={(e) => handleReagentProjectChange(index, 'category', e.target.value)}
                            required
                          >
                            {reagentCategoryOptions.map((category) => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>试剂名称</Form.Label>
                          <Form.Control
                            value={reagentProject.name}
                            onChange={e => handleReagentProjectChange(index, 'name', e.target.value)}
                            placeholder="请输入试剂名称（选填）"
                          />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                          <Form.Label>试剂图片</Form.Label>
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const target = e.target as HTMLInputElement;
                              const file = target.files?.[0];
                              if (!file) return;
                              try {
                                const imageUrl = await uploadApprovalImage(file);
                                handleReagentProjectChange(index, 'image', imageUrl);
                              } catch (error: any) {
                                showFeedback(error?.message || '图片上传失败，请重试。', '上传失败');
                              }
                            }}
                          />
                          <Form.Text className="text-muted">
                            上传试剂的图片（可选）
                          </Form.Text>
                        </Form.Group>
                        
                        <div className="row">
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>CAS号 *</Form.Label>
                              <Form.Control
                                value={reagentProject.cas}
                                onChange={e => handleReagentProjectChange(index, 'cas', e.target.value)}
                                placeholder="请输入CAS号"
                                required
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>货号</Form.Label>
                              <Form.Control
                                value={reagentProject.catalogNumber || ''}
                                onChange={e => handleReagentProjectChange(index, 'catalogNumber', e.target.value)}
                                placeholder="请输入货号（选填）"
                              />
                            </Form.Group>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>品牌 *</Form.Label>
                              <Form.Select
                                value={reagentProject.vendor}
                                onChange={e => handleReagentProjectChange(index, 'vendor', e.target.value)}
                                required
                              >
                                <option value="">请选择品牌</option>
                                {getReagentBrandOptions(reagentProject.vendor).map((brand) => (
                                  <option key={brand} value={brand}>
                                    {brand}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>规格 *</Form.Label>
                              <Form.Control
                                value={reagentProject.specification}
                                onChange={e => handleReagentProjectChange(index, 'specification', e.target.value)}
                                placeholder="请输入规格"
                                required
                              />
                            </Form.Group>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>单位 *</Form.Label>
                              <Form.Control
                                value={reagentProject.unit}
                                onChange={e => handleReagentProjectChange(index, 'unit', e.target.value)}
                                placeholder="请输入单位（如：g、ml、L等）"
                                required
                              />
                            </Form.Group>
                          </div>
                        </div>
                        
                        <div className="row">
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                            </Form.Group>
                          </div>
                        </div>
                        
                        <div className="row">
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>购买渠道/链接 *</Form.Label>
                              <Form.Control
                                value={reagentProject.purchaseChannel}
                                onChange={e => handleReagentProjectChange(index, 'purchaseChannel', e.target.value)}
                                placeholder="请输入购买渠道或链接"
                                required
                              />
                            </Form.Group>
                          </div>
                        </div>
                        
                        <div className="row">
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>申请数量 *</Form.Label>
                              <Form.Control
                                type="text"
                                inputMode="numeric"
                                value={reagentProject.amount}
                                onChange={e => handleReagentProjectChange(index, 'amount', e.target.value)}
                                onBlur={(e) => {
                                  const v = e.target.value.trim();
                                  if (v === '') return;
                                  const n = Math.max(1, Math.round(parseFloat(v) || 0));
                                  if (String(n) !== v) {
                                    handleReagentProjectChange(index, 'amount', String(n));
                                  }
                                }}
                                placeholder="请输入申请数量（正整数）"
                                required
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>单价（元）</Form.Label>
                              <Form.Control
                                type="text"
                                inputMode="decimal"
                                value={reagentProject.price || ''}
                                onChange={e => handleReagentProjectChange(index, 'price', e.target.value)}
                                onBlur={(e) => {
                                  const v = e.target.value.trim();
                                  if (v === '') return;
                                  const n = parseFloat(v.replace(/,/g, ''));
                                  if (!Number.isFinite(n) || n < 0) return;
                                  const fixed = (Math.round(n * 100) / 100).toFixed(2);
                                  if (fixed !== v) {
                                    handleReagentProjectChange(index, 'price', fixed);
                                  }
                                }}
                                placeholder="请输入单价"
                              />
                            </Form.Group>
                          </div>
                        </div>
                        
                        {reagentProject.price && reagentProject.amount && (
                          <div className="row">
                            <div className="col-md-6">
                              <Form.Group className="mb-3">
                                <Form.Label>总价（元）</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={reagentProject.total || ''}
                                  readOnly
                                  className="bg-light"
                                />
                              </Form.Group>
                            </div>
                          </div>
                        )}
                        
                        <Form.Group className="mb-3">
                          <Form.Label>使用说明</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={reagentProject.usage || ''}
                            onChange={e => handleReagentProjectChange(index, 'usage', e.target.value)}
                            placeholder="请说明试剂的使用目的和用途"
                          />
                        </Form.Group>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </>
            )}

            {/* 测试申请专用字段 */}
            {selectedApplicationType === '测试申请' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>测试平台 *</Form.Label>
                  <Form.Control
                    value={applicationForm.testPlatform}
                    onChange={e => handleTestFieldChange('testPlatform', e.target.value)}
                    placeholder="请输入测试平台名称"
                    required
                  />
                </Form.Group>
                
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="mb-0">测试项目 *</Form.Label>
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={addTestProject}
                    >
                      + 添加项目
                    </Button>
                  </div>
                  
                  {applicationForm.testProjects.map((testProject, index) => (
                    <Card key={index} className="mb-3" style={{ border: '1px solid #dee2e6' }}>
                      <Card.Header className="py-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-bold">测试项目 {index + 1}</span>
                          {applicationForm.testProjects.length > 1 && (
                            <Button 
                              variant="outline-danger" 
                              size="sm" 
                              onClick={() => removeTestProject(index)}
                            >
                              删除
                            </Button>
                          )}
                        </div>
                      </Card.Header>
                      <Card.Body className="py-3">
                        <Form.Group className="mb-3">
                          <Form.Label>项目名称 *</Form.Label>
                          <Form.Control
                            value={testProject.project}
                            onChange={e => handleTestProjectChange(index, 'project', e.target.value)}
                            placeholder="请输入测试项目名称"
                            required
                          />
                        </Form.Group>
                        <div className="row">
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>单价（元） *</Form.Label>
                              <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                value={testProject.unitPrice}
                                onChange={e => handleTestProjectChange(index, 'unitPrice', e.target.value)}
                                placeholder="请输入单价"
                                required
                              />
                            </Form.Group>
                          </div>
                          <div className="col-md-6">
                            <Form.Group className="mb-3">
                              <Form.Label>数量 *</Form.Label>
                              <Form.Control
                                type="number"
                                min="1"
                                value={testProject.quantity}
                                onChange={e => handleTestProjectChange(index, 'quantity', e.target.value)}
                                placeholder="请输入数量"
                                required
                              />
                            </Form.Group>
                          </div>
                        </div>
                        <Form.Group className="mb-0">
                          <Form.Label>项目总金额（元）</Form.Label>
                          <Form.Control
                            type="text"
                            value={testProject.totalAmount}
                            readOnly
                            className="bg-light"
                            placeholder="自动计算"
                          />
                        </Form.Group>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
                
                <Form.Group className="mb-3">
                  <Form.Label>总金额（元）</Form.Label>
                  <Form.Control
                    type="text"
                    value={applicationForm.testTotalAmount}
                    readOnly
                    className="bg-light fw-bold"
                    placeholder="自动计算"
                  />
                  <Form.Text className="text-muted">
                    所有测试项目的总金额（自动计算）
                  </Form.Text>
                </Form.Group>
              </>
            )}

            {/* 出差申请专用字段 */}
            {selectedApplicationType === '出差申请' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>出差目的地 *</Form.Label>
                  <Form.Control
                    value={applicationForm.destination}
                    onChange={e => handleTestFieldChange('destination', e.target.value)}
                    placeholder="请输入出差目的地"
                    required
                  />
                </Form.Group>
                
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>出发日期 *</Form.Label>
                      <Form.Control
                        type="date"
                        value={applicationForm.tripStartDate}
                        onChange={e => handleTestFieldChange('tripStartDate', e.target.value)}
                        required
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>返回日期 *</Form.Label>
                      <Form.Control
                        type="date"
                        value={applicationForm.tripEndDate}
                        onChange={e => handleTestFieldChange('tripEndDate', e.target.value)}
                        required
                      />
                    </Form.Group>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>出差天数</Form.Label>
                      <Form.Control
                        type="text"
                        value={applicationForm.tripDays}
                        readOnly
                        className="bg-light"
                        placeholder="自动计算"
                      />
                      <Form.Text className="text-muted">
                        根据出发和返回日期自动计算
                      </Form.Text>
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>预算金额（元）</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={applicationForm.tripBudget}
                        onChange={e => handleTestFieldChange('tripBudget', e.target.value)}
                        placeholder="请输入预算金额"
                      />
                    </Form.Group>
                  </div>
                </div>
                
                <Form.Group className="mb-3">
                  <Form.Label>出差事由 *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={applicationForm.tripPurpose}
                    onChange={e => handleTestFieldChange('tripPurpose', e.target.value)}
                    placeholder="请详细说明出差事由和目的"
                    required
                  />
                </Form.Group>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowApplicationModal(false)}>取消</Button>
          <Button variant="primary" disabled={reasonImagesUploading} onClick={handleSubmitApplication}>
            {reasonImagesUploading ? '图片上传中…' : '提交申请'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showChannelModal}
        onHide={() => {
          setShowChannelModal(false);
          setPendingCategory('');
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>选择申请方式 · {pendingCategory}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">请选择内部申请或向已授权商家提交申请。商家申请仍需先完成团队内部审批。</p>
          <div className="d-grid gap-2">
            <Button variant="outline-primary" size="lg" onClick={handleChooseInternal}>
              内部申请
            </Button>
            <Button
              variant="primary"
              size="lg"
              disabled={loadingMerchants}
              onClick={() => void handleChooseMerchantChannel()}
            >
              {loadingMerchants ? '加载商家中…' : '商家申请'}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showMerchantPickModal}
        onHide={() => {
          setShowMerchantPickModal(false);
          setPendingCategory('');
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>选择商家 · {pendingCategory}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">仅显示已授权给本团队的商家。</p>
          <div className="d-grid gap-2">
            {authorizedMerchants.map((m) => (
              <Button
                key={m._id}
                variant="outline-secondary"
                className="text-start"
                onClick={() => openFormAfterChannel('merchant', m._id, m.name)}
              >
                {m.name}
              </Button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            onClick={() => {
              setShowMerchantPickModal(false);
              setShowChannelModal(true);
            }}
          >
            返回
          </Button>
        </Modal.Footer>
      </Modal>

      <LimsFeedbackModal
        show={!!feedbackModal}
        onHide={() => setFeedbackModal(null)}
        title={feedbackModal?.title}
        message={feedbackModal?.message ?? ''}
      />
    </div>
  );
};

export default ApplicationSubmission;
